import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { logger } from "../utils/logger";
import { getUserRoles } from '../utils/roleHelpers';
import { userHoldsProviderSignupStaffRole } from '../../shared/providerAcquisition/providerSignupGate';
// import { logger } from "../utils/logger";
interface ProviderAccessStatus {
    canCreateEvents: boolean;
    applicationStatus: 'pending' | 'approved' | 'rejected' | null;
    submittedAt: string | null;
    reviewedAt: string | null;
    agencyName: string | null;
    rejectionReason: string | null;
    publishedEventsCount: number;
    /** Provider apply lite: HQ profile fields are still missing (step 2 not done). */
    profileIncomplete: boolean;
    /** Apply door the provider came through ('host' | 'instructor'), when recorded. */
    applicationRole: string | null;
    loading: boolean;
    error: string | null;
}
export const useProviderAccess = () => {
    const { user, isAuthenticated } = useAuth();
    const [accessStatus, setAccessStatus] = useState<ProviderAccessStatus>({
        canCreateEvents: false,
        applicationStatus: null,
        submittedAt: null,
        reviewedAt: null,
        agencyName: null,
        rejectionReason: null,
        publishedEventsCount: 0,
        profileIncomplete: false,
        applicationRole: null,
        loading: true,
        error: null
    });
    const checkProviderAccess = useCallback(async () => {
        // Allow training managers and coordinators to create events without provider status
        if (!isAuthenticated || !user?.id) {
            setAccessStatus(prev => ({
                ...prev,
                loading: false,
                canCreateEvents: false,
                applicationStatus: null
            }));
            return;
        }
        // Training managers and coordinators can always create events (no provider application UI)
        if (
            userHoldsProviderSignupStaffRole({
                role: user.role,
                roles: getUserRoles(user),
            })
        ) {
            setAccessStatus(prev => ({
                ...prev,
                loading: false,
                canCreateEvents: true,
                applicationStatus: null,
                publishedEventsCount: 0,
                profileIncomplete: false,
            }));
            return;
        }
        // Provider applicants register as instructor; fetch status for any non-staff user
        try {
            setAccessStatus(prev => ({ ...prev, loading: true, error: null }));
            const response = await fetch(`/api/training-providers/status/${user.id}`, {
                credentials: 'include'
            });
            if (!response.ok) {
                if (response.status === 404) {
                    // No provider application found
                    setAccessStatus(prev => ({
                        ...prev,
                        loading: false,
                        canCreateEvents: false,
                        applicationStatus: null
                    }));
                    return;
                }
                throw new Error('Failed to check provider status');
            }
            const data = await response.json();
            setAccessStatus({
                canCreateEvents: data.data.canCreateEvents,
                applicationStatus: data.data.applicationStatus,
                submittedAt: data.data.submittedAt,
                reviewedAt: data.data.reviewedAt,
                agencyName: data.data.agencyName,
                rejectionReason: data.data.rejectionReason ?? null,
                publishedEventsCount: data.data.publishedEventsCount ?? 0,
                profileIncomplete: data.data.profileIncomplete === true,
                applicationRole: data.data.applicationRole ?? null,
                loading: false,
                error: null
            });
        }
        catch (error) {
            logger.error('Error checking provider access:', error, {});
            setAccessStatus(prev => ({
                ...prev,
                loading: false,
                error: error instanceof Error ? error.message : 'Failed to check access status'
            }));
        }
    }, [isAuthenticated, user]);
    // Check access when user changes
    useEffect(() => {
        checkProviderAccess();
    }, [checkProviderAccess]);
    // Refresh access status
    const refreshAccess = useCallback(() => {
        checkProviderAccess();
    }, [checkProviderAccess]);
    return {
        ...accessStatus,
        refreshAccess,
        isProvider:
            user?.role === 'provider' ||
            user?.role === 'instructor' ||
            accessStatus.applicationStatus !== null,
        isPendingProvider: accessStatus.applicationStatus === 'pending',
        isApprovedProvider: accessStatus.applicationStatus === 'approved',
        isRejectedProvider: accessStatus.applicationStatus === 'rejected',
    };
};
